import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Shield, CheckCircle, Upload, Loader, AlertTriangle, Camera, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_LABELS = {
  commercial_property: "Commercial Property",
  workers_comp: "Workers' Compensation",
  habitational: "Habitational",
  general_liability: "General Liability",
  commercial_auto: "Commercial Auto",
};

function QuestionField({ q, answer, onChange, onPhoto, uploadingId }) {
  const fileRef = useRef(null);
  const isUploading = uploadingId === q.id;

  if (q.type === "yes_no") {
    return (
      <div className="flex gap-3">
        {["Yes", "No", "N/A"].map(opt => (
          <button key={opt} type="button"
            onClick={() => onChange(q.id, opt)}
            data-testid={`q-${q.id}-${opt.toLowerCase()}`}
            className={`px-5 py-2 rounded-lg text-sm font-medium border transition-all ${answer === opt
              ? "bg-navy text-white border-navy"
              : "bg-white text-slate-600 border-slate-200 hover:border-navy hover:text-navy"}`}>
            {opt}
          </button>
        ))}
      </div>
    );
  }

  if (q.type === "select" && q.options?.length > 0) {
    return (
      <select value={answer || ""} onChange={e => onChange(q.id, e.target.value)}
        data-testid={`q-${q.id}-select`}
        className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 bg-white text-slate-700">
        <option value="">Select an option...</option>
        {q.options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }

  if (q.type === "number") {
    return (
      <input type="number" value={answer || ""} onChange={e => onChange(q.id, e.target.value)}
        data-testid={`q-${q.id}-number`}
        placeholder={q.placeholder || "Enter a number"}
        className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30" />
    );
  }

  if (q.type === "photo") {
    return (
      <div>
        <input type="file" ref={fileRef} accept="image/*" className="hidden"
          onChange={e => { if (e.target.files[0]) onPhoto(q.id, e.target.files[0]); }} />
        <button type="button" onClick={() => fileRef.current?.click()}
          data-testid={`q-${q.id}-upload`}
          className="flex items-center gap-2 px-4 py-2 border border-dashed border-slate-300 rounded-lg text-sm text-slate-500 hover:border-navy hover:text-navy transition-colors">
          {isUploading ? <Loader className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          {isUploading ? "Uploading..." : answer ? "Photo uploaded — click to replace" : "Upload photo"}
        </button>
        {answer && !isUploading && (
          <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
            <CheckCircle className="w-3 h-3" /> {answer}
          </p>
        )}
      </div>
    );
  }

  // Default: text / textarea
  if (q.long) {
    return (
      <textarea value={answer || ""} onChange={e => onChange(q.id, e.target.value)}
        data-testid={`q-${q.id}-textarea`}
        rows={3} placeholder={q.placeholder || "Your answer..."}
        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 resize-none" />
    );
  }
  return (
    <input type="text" value={answer || ""} onChange={e => onChange(q.id, e.target.value)}
      data-testid={`q-${q.id}-text`}
      placeholder={q.placeholder || "Your answer..."}
      className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30" />
  );
}

export default function SurveyFormPortal() {
  const { token } = useParams();
  const [inspection, setInspection] = useState(null);
  const [template, setTemplate] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [uploadingId, setUploadingId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/insured/${token}`)
      .then(async res => {
        const insp = res.data;
        setInspection(insp);
        if (insp.status === "completed" || insp.status === "finalized") {
          setSubmitted(true);
        }
        if (insp.template_id) {
          try {
            const tmplRes = await axios.get(`${API}/insured/${token}/template`);
            setTemplate(tmplRes.data);
          } catch {}
        }
      })
      .catch(err => setError(err.response?.data?.detail || "Survey not found or link expired"))
      .finally(() => setLoading(false));
  }, [token]);

  const handleChange = (qId, value) => {
    setAnswers(prev => ({ ...prev, [qId]: value }));
  };

  const handlePhoto = async (qId, file) => {
    setUploadingId(qId);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      fd.append("message", `Photo for: ${template?.questions?.find(q => q.id === qId)?.text || qId}`);
      await axios.post(`${API}/insured/${token}/upload-photo`, fd);
      setAnswers(prev => ({ ...prev, [qId]: file.name }));
    } catch {
      setError("Photo upload failed. Please try again.");
    } finally {
      setUploadingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const answerList = (template?.questions || []).map(q => ({
        question_id: q.id,
        question: q.text,
        value: answers[q.id] || "(no answer)",
      }));
      await axios.post(`${API}/insured/${token}/submit-form`, { answers: answerList });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const progress = template?.questions?.length
    ? Math.round((Object.keys(answers).filter(k => answers[k]).length / template.questions.length) * 100)
    : 0;

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader className="w-6 h-6 animate-spin text-navy" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="text-center max-w-sm">
        <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <h2 className="font-heading font-bold text-slate-800 text-lg mb-2">Survey Not Found</h2>
        <p className="text-slate-500 text-sm">{error}</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="font-heading font-bold text-slate-800 text-2xl mb-2">Survey Complete</h2>
        <p className="text-slate-500">Thank you for completing the loss control survey. Your responses have been submitted to your insurance carrier for review.</p>
      </div>
    </div>
  );

  const carrierLogoUrl = inspection?.carrier_logo
    ? `${BACKEND_URL}${inspection.carrier_logo}`
    : null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-navy text-white px-4 py-5">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          {carrierLogoUrl ? (
            <img src={carrierLogoUrl} alt="Carrier logo" className="h-10 max-w-[120px] object-contain bg-white rounded p-1" />
          ) : (
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
          )}
          <div>
            <div className="font-heading font-bold text-white text-base">{inspection?.carrier_name || "Loss Control Survey"}</div>
            <div className="text-white/60 text-xs">{TYPE_LABELS[inspection?.inspection_type] || "Survey"} — {inspection?.insured_name}</div>
          </div>
        </div>
        {/* Progress bar */}
        <div className="max-w-2xl mx-auto mt-4">
          <div className="flex items-center justify-between text-xs text-white/60 mb-1">
            <span>{Object.keys(answers).filter(k => answers[k]).length} of {template?.questions?.length || 0} answered</span>
            <span>{progress}%</span>
          </div>
          <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
            <div className="h-full bg-white rounded-full transition-all duration-500" style={{ width: `${progress}%` }} data-testid="progress-bar" />
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        {!template ? (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
            <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">No form template assigned to this survey.</p>
            <p className="text-slate-400 text-sm mt-1">Please contact your insurance carrier.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
              <h2 className="font-heading font-bold text-slate-800 text-lg">{template.name}</h2>
              {template.description && <p className="text-slate-500 text-sm mt-1">{template.description}</p>}
            </div>

            {template.questions?.map((q, idx) => (
              <div key={q.id} className="bg-white border border-slate-200 rounded-xl p-5 space-y-3" data-testid={`question-${q.id}`}>
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-navy/10 text-navy text-xs font-bold rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">{idx + 1}</span>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800">{q.text}
                      {q.required && <span className="text-red-500 ml-1">*</span>}
                    </p>
                    {q.hint && <p className="text-xs text-slate-400 mt-0.5">{q.hint}</p>}
                  </div>
                </div>
                <div className="ml-9">
                  <QuestionField q={q} answer={answers[q.id]} onChange={handleChange} onPhoto={handlePhoto} uploadingId={uploadingId} />
                </div>
              </div>
            ))}

            <button type="submit" disabled={submitting} data-testid="submit-form-btn"
              className="w-full flex items-center justify-center gap-2 bg-navy text-white py-3.5 rounded-xl font-semibold text-sm hover:bg-navy-dark transition-colors disabled:opacity-60">
              {submitting ? <Loader className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
              {submitting ? "Submitting Survey..." : "Submit Survey"}
            </button>

            <p className="text-center text-xs text-slate-400">
              By submitting this form, you confirm that the information provided is accurate to the best of your knowledge.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
